import React, { useEffect, useState } from "react";
import moment from "moment-timezone";

import FavoriteBorderSharpIcon from '@material-ui/icons/FavoriteBorderSharp';
import FavoriteSharpIcon from '@material-ui/icons/FavoriteSharp';
import SettingsIcon from "@material-ui/icons/Settings";
import WifiOffIcon from '@material-ui/icons/WifiOff';
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Box from "@material-ui/core/Box";

// core components
import { DEFAULT_TIMEZONE, DEFAULT_TIMEOFFSET } from "../../common/constants";
import Button from "components/CustomButtons/Button.js";
import GridItem from "components/Grid/GridItem.js";
import timezones from "../../common/timezones.json";
import Setting from "./Setting";

import heart_rate from "assets/img/heart_rate-2-512.png";
import stress from "assets/img/stress.png";
import motion from "assets/img/motion.png";
import lungs from "assets/img/resp.png";

// Add CSS for animations
const cardAnimationStyles = `
  .card-fade-in {
    animation: fadeIn 0.5s ease-in-out;
  }
  
  .card-fade-out {
    animation: fadeOut 0.3s ease-out;
  }
  
  .card-expand {
    animation: expand 0.4s ease-in-out;
  }
  
  .card-slide-out {
    animation: slideOut 0.4s ease-in-out forwards;
  }
  
  .card-slide-in {
    animation: slideIn 0.4s ease-in-out forwards;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.8);
    }
  }
  
  @keyframes expand {
    from {
      transform: scale(1);
    }
    to {
      transform: scale(1.1);
    }
  }
  
  @keyframes slideOut {
    from {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
    to {
      opacity: 0;
      transform: translateX(-20px) scale(0.8);
    }
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px) scale(0.8);
    }
    to {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = cardAnimationStyles;
  document.head.appendChild(styleElement);
}

const CardItem = ({imgSrc, label, value, inActiveCard, isExpanded, animationClass, viewMode}) => (
  <div 
    className={animationClass}
    style={{
      padding: "6px 0",
      border: "1px solid #ccc", 
      display: "flex", 
      flexDirection: "column", 
      width: "100%", 
      margin: viewMode === 'hrrr' ? "0 16px" : "0 2px", 
      alignItems: "center", 
      justifyContent: "center", 
      background: "#fff", 
      borderRadius: "5px",
      transition: "all 0.4s ease-in-out",
      transform: isExpanded ? "scale(1.025)" : "scale(1)"
    }}
  >
    <Avatar src={imgSrc} style={{ 
      verticalAlign: "middle", 
      margin: "auto", 
      height: "35px",
      width: "35px",
      filter: inActiveCard ? "grayscale(100%)" : "none",
      transition: "all 0.4s ease-in-out",
      transform: isExpanded ? "scale(1.2)" : "scale(1)"
    }} />
    <Typography style={{
      fontSize: isExpanded ? "11px" : "9px",
      fontWeight: "normal",
      color: "inherit",
      transition: "all 0.4s ease-in-out"
    }}>{label}</Typography>
    <Typography style={{
      fontSize: isExpanded ? "28px" : "24px", 
      fontWeight: "700",
      color: "inherit",
      transition: "all 0.4s ease-in-out"
    }}>{value}</Typography>
  </div>
)

const OnLineOffLineLabel = ({isOnline}) => (
  <Box textAlign="center">
    <div style={{ width: "10px", height: "10px", background: isOnline ? "green" : "red", borderRadius: "50%", display: "inline-block" }}></div>{" "}
    <b>{isOnline ? "Online" : "Offline"}</b>
  </Box>
)

const OccupiedLabel = ({isOccupiedSensor}) => (
  <Box textAlign="center">
    <div style={{ width: "10px", height: "10px", background: isOccupiedSensor ? "green" : "red", borderRadius: "50%", display: "inline-block" }}></div>{" "}
    <b>{isOccupiedSensor ? "Occupied" : "Unoccupied"}</b>
  </Box>
)

export default function DevicesNew({
  updateSensorControlTableRequest,
  updateSensorControlRequest,
  sensorControlRequest,
  setShowSensorControl,
  setDayLightSaving,
  showSensorControl,
  setUserMacAddress,
  startGraphLoader,
  setFriendlyName,
  setUserTimeZone,
  setAlertMessage,
  loggedUserRole,
  intervalChange,
  userMacAddress,
  setToastStatus,
  onIndexUpdate,
  friendlyName,
  itemsPerPage,
  userTimeZone,
  setUserInfo,
  currentPage,
  lastFilter,
  setSensor,
  setDrawer,
  setOffset,
  userData,
  userInfo,
  setAbbr,
  device,
  sensor,
  viewMode
}) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [previousViewMode, setPreviousViewMode] = useState(viewMode);

  // Handle view mode transitions
  useEffect(() => {
    if (previousViewMode !== viewMode) {
      setIsTransitioning(true);
      setPreviousViewMode(viewMode);
      
      // Reset transition state after animation completes
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    }
  }, [viewMode, previousViewMode]);

  return (
    <>
      {device.graphData.map((val, i) => {
        // Conditionally include Stress and Motion based on view mode
        const baseReadingParam = ["Hr", "Rr", "DevidPvital","Occupied"];
        const readingParam = viewMode === 'hrrr' ? baseReadingParam : [...baseReadingParam, "Stress", "Motion"];
        const userDayLightSaving = val.dayLightSaving || "No";

        let newData = {
          Hr: { value: "--" },
          Rr: { value: "--" },
          Stress: { value: "--" },
          Motion: { value: "--" },
          FriendlyName: { value: "--" },
          Location: { value: "--" },
          active: { value: "No" },
        };
        var isOccupied = false

        return (
          <GridItem xs={12} key={"deivce" + i}>
            {val.linkedGraphData && Object.keys(val.linkedGraphData).length > 0 ? <Box bgcolor="#ffffff" padding={1} borderRadius="5px" marginBottom={2}>
              <Typography noWrap={true} style={{ paddingTop: "2%", textAlign: "center" }}>
                <b>{val?.fullName}</b>
              </Typography>
              &nbsp;&nbsp;
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {val.linkedGraphData &&
                  Object.keys(val.linkedGraphData)
                    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                    .map((linkedData, index) => {
                      const linkedGraphDataVal = val?.linkedGraphData?.[linkedData];
                      const currentReading = linkedGraphDataVal?.data[0]?.data;
                      if (linkedGraphDataVal?.data?.length) {
                        isOccupied = currentReading?.Occupied?.value === 1 ? true : false
                        
                        newData = {
                          Stress: { value: currentReading?.Stress.value !== undefined ? currentReading?.Stress.value >= 10 ? Math.round(currentReading?.Stress.value): currentReading?.Stress.value.toFixed(1) : "--" },
                          Motion: { value: currentReading?.Motion.value !== undefined ? Math.round(currentReading?.Motion.value) : "--" },
                          Hr: { value: currentReading?.Hr.value !== undefined ? Math.round(currentReading?.Hr.value) : "--" },
                          Rr: { value: currentReading?.Rr.value !== undefined ? Math.round(currentReading?.Rr.value) : "--" },
                          FriendlyName: {value: linkedGraphDataVal?.FriendlyName},
                          Location: {value: linkedGraphDataVal?.Location},
                          active: {value: linkedGraphDataVal?.active},
                        };
                      } else {
                        newData = {
                          Hr: { value: "--" },
                          Rr: { value: "--" },
                          Stress: { value: "--" },
                          Motion: { value: "--" },
                          FriendlyName: { value: "--" },
                          Location: { value: "--" },
                          active: { value: "No" },
                        };
                      }
                      newData.FriendlyName.value = linkedGraphDataVal?.FriendlyName || "--";
                      newData.Location.value = linkedGraphDataVal?.Location || "--";
                      newData.active.value = linkedGraphDataVal?.active || "No";
                      
                      let mainSectionDesign = {
                        backgroundColor: "#eeeeee",
                        padding: "10px",
                        borderRadius: "10px",
                      };
                      let fwVersion = linkedGraphDataVal?.Fwvers || "";
                      let cardStyle = {
                        flex: '1 1 calc(20% - 8px)',
                        minWidth: '200px',
                        maxWidth: '250px'
                      };

                      if (newData.active.value === "No") {
                        mainSectionDesign.backgroundColor = "#ebd7d7";
                      }

                      const userTimeZone = linkedGraphDataVal?.timezone;
                      const selectedZone = timezones.find((time) => time.value === userTimeZone) || {};
                      selectedZone.offset = selectedZone.offset || DEFAULT_TIMEOFFSET;
                      selectedZone.abbr = selectedZone.abbr || DEFAULT_TIMEZONE;
                      if (!selectedZone.utc) {
                        selectedZone.utc = timezones.find((time) => time.abbr === DEFAULT_TIMEZONE).utc;
                      }
                      return (
                        <div key={"sensor" + index} style={cardStyle}>
                          <Box style={mainSectionDesign}>
                            <div style={{textAlign: "center",fontWeight: 600,fontSize: "12px"}}>
                              <Typography style={{fontSize: "13px", fontWeight: "700" }}>{newData.FriendlyName.value || "--"} ({newData.Location.value || "--"})</Typography>
                            </div>

                            <div style={{display: "flex", alignItems: "center", justifyContent: "space-evenly"}}>
                              {/* Online/Offline */}
                              <div className={currentReading?.Online?.value === 1 ?  isOccupied ? "heartBeatAnimation" : '' : ""}>
                                {currentReading?.Online?.value === 1 ? (isOccupied ? <FavoriteSharpIcon style={{ color: '#ed193b' }} /> : <FavoriteBorderSharpIcon />) : <WifiOffIcon />}
                              </div>
                              {currentReading?.Online?.value === 1 ? <OnLineOffLineLabel isOnline={true} /> : <OnLineOffLineLabel isOnline={false} />}
                              <OccupiedLabel isOccupiedSensor={isOccupied} />
                            </div>

                            <div style={{
                              display: "flex", 
                              margin: "8px 0",
                              transition: "all 0.4s ease-in-out",
                              justifyContent: viewMode === 'hrrr' ? 'space-around' : 'space-evenly'
                            }}>
                              {readingParam.map((val, index) => {
                                const isExpanded = viewMode === 'hrrr' && (val === "Hr" || val === "Rr");
                                let animationClass = 'card-fade-in';
                                
                                if (isTransitioning) {
                                  if (viewMode === 'hrrr' && (val === "Stress" || val === "Motion")) {
                                    animationClass = 'card-slide-out';
                                  } else if (previousViewMode === 'hrrr' && (val === "Stress" || val === "Motion")) {
                                    animationClass = 'card-slide-in';
                                  } else {
                                    animationClass = 'card-fade-out';
                                  }
                                } else if (viewMode === 'hrrr' && (val === "Hr" || val === "Rr")) {
                                  animationClass = 'card-expand';
                                } else if (viewMode === 'default' && (val === "Stress" || val === "Motion")) {
                                  animationClass = 'card-slide-in';
                                }
                                
                                if (val === "Hr") {
                                  return <CardItem 
                                    key={`hr-${index}`}
                                    inActiveCard={newData.active.value === "No"} 
                                    imgSrc={heart_rate} 
                                    label="Heart" 
                                    value={newData[val].value}
                                    isExpanded={isExpanded}
                                    animationClass={animationClass}
                                    viewMode={viewMode}
                                  />
                                } else if (val === "Rr") {
                                  return <CardItem 
                                    key={`rr-${index}`}
                                    inActiveCard={newData.active.value === "No"} 
                                    imgSrc={lungs} 
                                    label="Breath" 
                                    value={newData[val].value}
                                    isExpanded={isExpanded}
                                    animationClass={animationClass}
                                    viewMode={viewMode}
                                  />
                                } else if (val === "Stress") {
                                  return <CardItem 
                                    key={`stress-${index}`}
                                    inActiveCard={newData.active.value === "No"} 
                                    imgSrc={stress} 
                                    label="Stress" 
                                    value={newData[val] ? newData[val].value : "--"}
                                    isExpanded={isExpanded}
                                    animationClass={animationClass}
                                    viewMode={viewMode}
                                  />
                                } else if (val === "Motion") {
                                  return <CardItem 
                                    key={`motion-${index}`}
                                    inActiveCard={newData.active.value === "No"} 
                                    imgSrc={motion} 
                                    label="Motion" 
                                    value={newData[val] ? newData[val].value : "--"}
                                    isExpanded={isExpanded}
                                    animationClass={animationClass}
                                    viewMode={viewMode}
                                  />
                                }
                                return null;
                              })}
                            </div>
                            <Box style={{display: "flex", alignItems: "center", justifyContent: "space-evenly"}}>
                              {/* Index Selector */}
                              {
                                Object.keys(val.linkedGraphData).length > 1 && userInfo.userId !== 'all' &&
                                <div>
                                  <select style={{"width": "50px","height": "30px"}} value={((currentPage - 1) * itemsPerPage)+index+1} onChange={e => onIndexUpdate(e.target.value, val, linkedData)}>
                                    {Object.keys(val.linkedGraphData).map((linkedData, i) => {
                                      return <option value={i+1}>{i+1}</option>
                                    })}
                                  </select>
                                </div>
                              }
                              <Button
                                style={{"width": "100%","height": "30px", margin: "0 3px", maxWidth: "150px"}}
                                color="danger"
                                onClick={() => {
                                  const HRValues = [],
                                    RRValues = [],
                                    Timings = [];
                                  if (val?.linkedGraphData[linkedData]?.data?.length) {
                                    val.linkedGraphData[linkedData].data.forEach((val) => {
                                      Timings.push(val.data.Hr.valueProvidedOn);
                                      HRValues.push([new Date(val.data.Hr.valueProvidedOn), val.data.Hr.value]);
                                      RRValues.push([new Date(val.data.Hr.valueProvidedOn), val.data.Rr.value]);
                                    });
                                  } else {
                                    const currentTime = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
                                    HRValues.push([new Date(currentTime), 0]);
                                    RRValues.push([new Date(currentTime), 0]);
                                  }
                                  const graphIntervalObj = val.customFieldData.find(
                                    (val) => val.field.name === "Plot_range_default"
                                  );
                                  let obj = { interval: "2 Hr", devID: linkedData,};
                                  if (graphIntervalObj?.fieldData) {
                                    obj = {interval: "2 Hr", devID: linkedData};
                                  }
                                  setDrawer(true);
                                  setSensor(val);
                                  setUserInfo({ ...userInfo, ...obj });
                                  const lastFilterData = lastFilter;
                                  lastFilterData.devHealth.ownerFilter.users = [val.userId];
                                  lastFilterData.pVittal.ownerFilter.users = [val.userId];
                                  setTimeout(() => {
                                    intervalChange(selectedZone.utc[0], "", obj.interval, linkedData, val.userId, selectedZone.offset, userDayLightSaving === "Yes" ? 1 : 0);
                                  }, 100);
                                  startGraphLoader();
                                  setUserTimeZone(selectedZone.utc[0]);
                                  setOffset(selectedZone.offset);
                                  setAbbr(selectedZone.abbr);
                                  setDayLightSaving(userDayLightSaving === "Yes" ? 1 : 0);
                                  setFriendlyName(val.linkedGraphData[linkedData].FriendlyName);
                                }}
                              >
                                History
                              </Button>
                               {/* Settings */}
                              <Box
                                textAlign="center"
                                style={{fontSize: "11px", cursor: "pointer"}}
                                onClick={() => {
                                  let thresholdId = "d36a4373-fbdc-44a3-8c96-4bb920041e40-th";
                                  let identityId = "d36a4373-fbdc-44a3-8c96-4bb920041e40-id";
                                  let paramId = "d36a4373-fbdc-44a3-8c96-4bb920041e40-pa";
                                  let finalObj = [
                                    {
                                      ownerFilter: {users: [val.userId]},
                                      deviceDataModelId: identityId,
                                      deviceCriteria: [{key: "Devid", operator: "Equal", value: linkedData}],
                                    },
                                    {
                                      ownerFilter: {users: [val.userId]},
                                      deviceDataModelId: paramId,
                                      deviceCriteria: [{ key: "DevidParams", operator: "Equal", value: linkedData}],
                                    },
                                    {
                                      ownerFilter: {users: [val.userId]},
                                      deviceDataModelId: thresholdId,
                                      deviceCriteria: [{ key: "DevidThresholds", operator: "Equal", value: linkedData}],
                                    },
                                  ];
                                  setSensor(val);
                                  setUserTimeZone(selectedZone.utc[0]);
                                  setOffset(selectedZone.offset);
                                  setAbbr(selectedZone.abbr);
                                  sensorControlRequest(finalObj);
                                  setUserMacAddress(linkedData);
                                  setFriendlyName(val.linkedGraphData[linkedData].FriendlyName);
                                  setTimeout(() => {
                                    setShowSensorControl(true);
                                  }, 200);
                                }}
                              >
                                <SettingsIcon titleAccess="Device Settings" style={{verticalAlign: "middle", margin: "auto"}}/>
                              </Box>
                            </Box>
                            {fwVersion && <Typography style={{fontSize: "10px", textAlign: "center" }}>{fwVersion}</Typography>}
                          </Box>
                        </div>
                      );
                    }
                  )}

                {/* {(!val.linkedGraphData || Object.keys(val.linkedGraphData).length === 0) && (
                  <div style={{ flex: '1 1 100%', padding: "8px" }}>
                    <Box style={{backgroundColor: "#ebd7d7", borderRadius: "10px", paddingTop: "10px", paddingBottom: "10px"}}>
                      <Typography noWrap={true} style={{textAlign: "center", fontWeight: 600, fontSize: "12px"}}>--</Typography>
                      <Typography noWrap={true} style={{textAlign: "center", fontWeight: 600, fontSize: "12px"}}>--</Typography>
                      {readingParam.map((val) => {
                        if (val === "Hr") {
                          return <CardItem imgSrc={heart_rate} label="Heart" value={newData[val].value} viewMode={viewMode} />
                        } else if (val === "Rr") {
                          return <CardItem imgSrc={lungs} label="Breath" value={newData[val].value} viewMode={viewMode} />
                        } else if (val === "Stress") {
                          return <CardItem imgSrc={stress} label="Stress" value={newData[val] ? newData[val].value : "--"} viewMode={viewMode} />
                        } else if (val === "Motion") {
                          return <CardItem imgSrc={motion} label="Motion" value={newData[val] ? newData[val].value : "--"} viewMode={viewMode} />
                        }
                      })}
                      <Box style={{cursor: "pointer"}} marginTop="2%" textAlign="center" justifyContent="center">
                        <Button
                          size="sm"
                          color="danger"
                          onClick={() => {
                            const HRValues = [],
                              RRValues = [],
                              Timings = [];
                            if (val.graphData && val.graphData.length > 0) {
                              val.graphData.forEach((val) => {
                                Timings.push(val.data.Hr.valueProvidedOn);
                                HRValues.push([
                                  new Date(val.data.Hr.valueProvidedOn),
                                  val.data.Hr.value,
                                ]);
                                RRValues.push([
                                  new Date(val.data.Hr.valueProvidedOn),
                                  val.data.Rr.value,
                                ]);
                              });
                            } else {
                              const currentTime = moment().utc().format("YYYY-MM-DDTHH:mm:ss");
                              HRValues.push([new Date(currentTime), 0]);
                              RRValues.push([new Date(currentTime), 0]);
                            }
                            const graphIntervalObj = val.customFieldData.find(
                              (val) => val.field.name === "Plot_range_default"
                            );
                            let obj = {interval: "1 Hr"};
                            if (graphIntervalObj && graphIntervalObj.fieldData) {
                              obj = {interval: graphIntervalObj.fieldData};
                            }
                            setDrawer(true);
                            setSensor(val);
                            setUserInfo({ ...userInfo, ...obj });
                            setTimeout(() => {
                              resetGraphData();
                            }, 9000);
                            const newelectedZone =
                              timezones.find(
                                (time) => time.abbr === DEFAULT_TIMEZONE
                              ) || {};
                            setUserTimeZone(newelectedZone.utc[0]);
                            setOffset(newelectedZone.offset);
                            setAbbr(newelectedZone.abbr);
                            setDayLightSaving(
                              userDayLightSaving === "Yes" ? 1 : 0
                            );
                          }}
                        >
                          History
                        </Button>
                      </Box>
                      <Box textAlign="center">
                        <p style={{ fontSize: "11px" }}>
                          <b>Last updated on:</b>
                        </p>
                        {val.graphData && val.graphData.length > 0 ? (<p></p>) : (<p style={{ fontSize: "11px" }}>--</p>)}
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="center"
                      >
                        <OnLineOffLineLabel isOnline={false} />
                      </Box>
                    </Box>
                  </div>
                )} */}
              </div>
              <p className="deviceDisclaimer">To change the sequence of icons in the display, adjust the number in the icon from the left to right</p>
            </Box> : <div style={{flex: '1 1 100%', padding: "8px", textAlign: "center"}}>
              <Typography noWrap={true} style={{textAlign: "center", fontWeight: 600, fontSize: "16px"}}>No sensor to display</Typography>
            </div>}
          </GridItem>
        );
      })}
      {showSensorControl && (
        <Setting
          updateSensorControlTableRequest={updateSensorControlTableRequest}
          updateSensorControlRequest={updateSensorControlRequest}
          sensorControlRequest={sensorControlRequest}
          setShowSensorControl={setShowSensorControl}
          showSensorControl={showSensorControl}
          setUserMacAddress={setUserMacAddress}
          setFriendlyName={setFriendlyName}
          setAlertMessage={setAlertMessage}
          setUserTimeZone={setUserTimeZone}
          userMacAddress={userMacAddress}
          setToastStatus={setToastStatus}
          loggedUserRole={loggedUserRole}
          friendlyName={friendlyName}
          userTimeZone={userTimeZone}
          setOffset={setOffset}
          userData={userData}
          setAbbr={setAbbr}
          sensor={sensor}
          device={device}
        />
      )}
    </>
  );
}